// src/components/Salary/SalaryDeduction.jsx
//
// Salary -> Salary Deduction.
// Mirrors Collection's "Employee Deduction": that posts a CREDIT to the
// "Short/Excess Account" Other Ledger (shortage recovered); a Salary Deduction
// has no POS source, so it is entered directly here, but posts the SAME way -
// a CREDIT to the same "Short/Excess Account", for the chosen company
// (server/controllers/salaryDeductionController.js). The statement below the
// form is laid out exactly like Finance -> Other Ledgers (same CSS), but reads
// a dedicated, FILTERED endpoint (GET /salary-deductions/ledger): only Employee
// Deduction entries from Collection and entries added on this page are shown -
// a cashier's Short/Excess entries on that same shared account are excluded.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinusCircle, faSpinner, faCheck, faBookOpen, faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import api from '../../api';
import { usePopup } from '../context/PopupContext';
import useDialog from '../common/ui/useDialog';
import '../Finance/FinanceForm.css';
import '../Finance/FinanceLedger.css';
import '../Finance/StaffLoans.css';
import './SalaryEntry.css';

const WRITE_ROLES = ['Admin', 'Super Admin', 'Accountant'];

// Must match server/services/otherLedgerService.js SHORT_EXCESS_ACCOUNT_NAME.
const SHORT_EXCESS_ACCOUNT_NAME = 'Short/Excess Account';

const getStoredUserRole = () => {
  try {
    const storedUser = localStorage.getItem('user');
    const role = storedUser ? JSON.parse(storedUser).role : null;
    return role === 'QB' ? 'View Only' : role;
  } catch (e) {
    return null;
  }
};

const todayLocal = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const fmt = (v) => parseFloat(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : (iso || '');
};

// Same map OtherLedgers.jsx uses, plus this page's own source.
const SOURCE_LABELS = {
  CASH_RECEIVE: 'Cash Received',
  CASH_ISSUED: 'Cash Issued',
  BANK_RECEIVE: 'Bank Received',
  BANK_ISSUED: 'Bank Issued',
  COLLECTION_AR: 'AR Collection',
  COLLECTION_SHORT_EXCESS: 'Short / Excess',
  COLLECTION_AR_SETTLEMENT: 'AR Settled (Credit Payment)',
  SALARY_DEDUCTION: 'Salary Deduction',
  SALARY_ENTRY_DEDUCTION: 'Salary Deduction (Salary Entry)'
};

const emptyForm = () => ({ employee_name: '', amount: '', deduction_date: todayLocal(), remark: '' });

const SalaryDeduction = () => {
  const { showPopup } = usePopup();
  const canWrite = WRITE_ROLES.includes(getStoredUserRole());

  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [filters, setFilters] = useState({ dateFrom: '', dateTo: '', search: '', sourceType: '', direction: '' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState(emptyForm());
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const addDialogRef = useRef(null);
  const closeAddModal = () => { if (!saving) setShowAddModal(false); };
  useDialog(addDialogRef, { open: showAddModal, onClose: closeAddModal });

  useEffect(() => {
    api.get('/companies')
      .then((res) => {
        const list = res.data || [];
        setCompanies(list);
        if (list.length > 0) setCompanyId(String(list[0].id));
      })
      .catch(() => setCompanies([]));
  }, []);

  // Employee Deduction entries from Collection + entries added on this page -
  // cashier Short/Excess entries on the same account are excluded server-side
  // (server/controllers/salaryDeductionController.js getSalaryDeductionLedger).
  const fetchTransactions = useCallback(() => {
    if (!companyId) { setData(null); return; }
    setLoading(true);
    setError('');
    const params = { company_id: companyId };
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;
    api.get('/salary-deductions/ledger', { params })
      .then((res) => setData(res.data))
      .catch((err) => { setData(null); setError(err.response?.data?.error || 'Failed to load Salary Deduction ledger'); })
      .finally(() => setLoading(false));
    // Only the date range is sent to the server; Type/Direction/Search below
    // filter the already-fetched result, so they must not trigger a refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, filters.dateFrom, filters.dateTo]);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  const transactions = data?.transactions || [];

  // Same effect as Cash Ledger's Type/Direction/Reference filters - applied
  // here to the already date-filtered result set instead of the server.
  const visibleTransactions = transactions.filter((t) => {
    if (filters.sourceType && t.source_type !== filters.sourceType) return false;
    if (filters.direction && (t.direction || '').toUpperCase() !== filters.direction) return false;
    if (filters.search.trim()) {
      const term = filters.search.trim().toLowerCase();
      const haystack = `${t.reference_no || ''} ${t.remark || ''}`.toLowerCase();
      if (!haystack.includes(term)) return false;
    }
    return true;
  });

  const totals = visibleTransactions.reduce((acc, t) => ({
    debit: acc.debit + (parseFloat(t.debit) || 0),
    credit: acc.credit + (parseFloat(t.credit) || 0)
  }), { debit: 0, credit: 0 });

  const money = (v, cls) => (parseFloat(v || 0) > 0 ? <span className={cls}>{fmt(v)}</span> : <span className="fl-empty-val">—</span>);

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validateForm = () => {
    const errors = {};
    if (!companyId) errors.company = 'Please select a company';
    if (!form.employee_name.trim()) errors.employee_name = 'Employee Name or Type is required';

    const amount = parseFloat(form.amount);
    if (!form.amount || Number.isNaN(amount) || amount <= 0) errors.amount = 'Amount must be greater than zero';

    if (!form.deduction_date) errors.deduction_date = 'Date is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (saving || !validateForm()) return;

    setSaving(true);
    try {
      await api.post('/salary-deductions', {
        company_id: Number(companyId),
        employee_name: form.employee_name.trim(),
        amount: form.amount,
        deduction_date: form.deduction_date,
        remark: form.remark.trim()
      });
      showPopup('success', 'Salary deduction saved successfully');
      setForm(emptyForm());
      setFormErrors({});
      setShowAddModal(false);
      await fetchTransactions();
    } catch (err) {
      showPopup('error', err.response?.data?.error || 'Failed to save salary deduction');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-container cash-ledger-page">
      <header className="page-header">
        <h2 style={{ textAlign: 'left', margin: 0 }}>
          <FontAwesomeIcon icon={faMinusCircle} style={{ marginRight: '8px' }} /> Salary Deduction
        </h2>
        <div className="cash-ledger-header-actions">
          <label htmlFor="sd-company" style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', whiteSpace: 'nowrap' }}>Company:</label>
          <select id="sd-company" value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="report-filter-select">
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {canWrite && (
            <button type="button" className="btn btn-primary" onClick={() => setShowAddModal(true)}>
              <FontAwesomeIcon icon={faPlus} /> Add Salary Deduction
            </button>
          )}
        </div>
      </header>

      {/* ════════════ Add Salary Deduction popup (posts a CREDIT to Short/Excess Account) ════════════ */}
      {showAddModal && (
        <div className="sl-modal-overlay" onClick={closeAddModal}>
          <div ref={addDialogRef} className="sl-modal" role="dialog" aria-modal="true" aria-labelledby="sd-add-title" onClick={(e) => e.stopPropagation()}>
            <div className="sl-modal-header">
              <h3 id="sd-add-title">
                <FontAwesomeIcon icon={faMinusCircle} style={{ marginRight: '8px' }} />
                Add Salary Deduction
              </h3>
              <button type="button" className="sl-modal-close" onClick={closeAddModal} aria-label="Close">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <form onSubmit={handleSave} noValidate>
              <div className="sl-modal-body">
                <div className="form-group">
                  <label htmlFor="sd-employee">Employee Name or Type <span className="required">*</span></label>
                  <input
                    id="sd-employee"
                    type="text"
                    name="employee_name"
                    value={form.employee_name}
                    onChange={handleFormChange}
                    placeholder="e.g. employee name or deduction type"
                    className="form-input"
                    maxLength={150}
                    autoFocus
                  />
                  {formErrors.employee_name && <span className="form-error">{formErrors.employee_name}</span>}
                </div>

                <div className="form-group" style={{ marginTop: 'var(--spacing-lg)' }}>
                  <label htmlFor="sd-amount">Amount (Rs.) <span className="required">*</span></label>
                  <input
                    id="sd-amount"
                    type="number"
                    name="amount"
                    min="0.01"
                    step="0.01"
                    inputMode="decimal"
                    value={form.amount}
                    onChange={handleFormChange}
                    placeholder="0.00"
                    className="form-input"
                  />
                  {formErrors.amount && <span className="form-error">{formErrors.amount}</span>}
                </div>

                <div className="form-group" style={{ marginTop: 'var(--spacing-lg)' }}>
                  <label htmlFor="sd-date">Date <span className="required">*</span></label>
                  <input
                    id="sd-date"
                    type="date"
                    name="deduction_date"
                    value={form.deduction_date}
                    onChange={handleFormChange}
                    className="form-input"
                  />
                  {formErrors.deduction_date && <span className="form-error">{formErrors.deduction_date}</span>}
                </div>

                <div className="form-group" style={{ marginTop: 'var(--spacing-lg)' }}>
                  <label htmlFor="sd-remark">Remark</label>
                  <input
                    id="sd-remark"
                    type="text"
                    name="remark"
                    value={form.remark}
                    onChange={handleFormChange}
                    placeholder="Optional"
                    className="form-input"
                    maxLength={255}
                  />
                </div>

                {formErrors.company && <div className="form-error" style={{ marginTop: 'var(--spacing-sm)' }}>{formErrors.company}</div>}
              </div>

              <div className="sl-modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeAddModal} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</> : <><FontAwesomeIcon icon={faCheck} /> Save Salary Deduction</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ════════════ Short/Excess Account statement - same layout as Finance -> Other Ledgers ════════════ */}
      {data && (
        <div className="cash-ledger-kpi-row">
          <div className="card cash-ledger-balance-card" style={{ display: 'flex', alignItems: 'center', padding: 'var(--spacing-lg)', gap: 'var(--spacing-xl)', marginBottom: 'var(--spacing-md)' }}>
            <div className="cash-ledger-balance-icon"><FontAwesomeIcon icon={faBookOpen} /></div>
            <div style={{ flex: 1 }}>
              <div className="cash-ledger-balance-label">
                {SHORT_EXCESS_ACCOUNT_NAME} Balance{companies.find((c) => String(c.id) === String(companyId)) ? ` — ${companies.find((c) => String(c.id) === String(companyId)).name}` : ''}
              </div>
              <div className="cash-ledger-balance-value" style={{ margin: 'var(--spacing-xs) 0' }}>
                Rs. {fmt(data.balance)}
              </div>
            </div>
          </div>
          <div className="cash-ledger-summary" style={{ display: 'flex', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-lg)' }}>
            <div className="card cash-ledger-total-card cash-ledger-debit-card" style={{ flex: 1, padding: 'var(--spacing-lg)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
              <span className="cash-ledger-total-label">Total Debit</span>
              <strong className="cash-ledger-total-value">Rs. {fmt(totals.debit)}</strong>
            </div>
            <div className="card cash-ledger-total-card cash-ledger-credit-card" style={{ flex: 1, padding: 'var(--spacing-lg)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
              <span className="cash-ledger-total-label">Total Credit</span>
              <strong className="cash-ledger-total-value">Rs. {fmt(totals.credit)}</strong>
            </div>
          </div>
        </div>
      )}

      <div className="card filters-section report-filter-bar">
        <div className="ledger-filter-row">
          <input type="date" value={filters.dateFrom} onChange={(e) => setFilters((p) => ({ ...p, dateFrom: e.target.value }))} title="Date From" className="report-filter-input" />
          <input type="date" value={filters.dateTo} onChange={(e) => setFilters((p) => ({ ...p, dateTo: e.target.value }))} title="Date To" className="report-filter-input" />
          <input type="text" value={filters.search} onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))} placeholder="Reference / Remark" className="report-filter-input" />
          <select value={filters.sourceType} onChange={(e) => setFilters((p) => ({ ...p, sourceType: e.target.value }))} className="report-filter-select">
            <option value="">All Types</option>
            {Object.entries(SOURCE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <select value={filters.direction} onChange={(e) => setFilters((p) => ({ ...p, direction: e.target.value }))} className="report-filter-select">
            <option value="">All Directions</option>
            <option value="IN">IN</option>
            <option value="OUT">OUT</option>
          </select>
        </div>
      </div>

      {error && <div className="sl-modal-error" role="alert" style={{ marginBottom: 'var(--spacing-md)' }}>{error}</div>}

      <div className="card cash-ledger-table-card other-ledger-table-card">
        {loading ? (
          <div style={{ textAlign: 'center', padding: 'var(--spacing-2xl)', color: 'var(--color-text-secondary)' }}><FontAwesomeIcon icon={faSpinner} spin /> Loading…</div>
        ) : (
          <table className="report-table cash-ledger-table other-ledger-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Company</th>
                <th>Type</th>
                <th>Direction</th>
                <th>Reference</th>
                <th>Remark</th>
                <th className="fl-num">Debit</th>
                <th className="fl-num">Credit</th>
                <th className="fl-num">Balance</th>
              </tr>
            </thead>
            <tbody>
              {data && Number(data.opening_balance) !== 0 && (
                <tr>
                  <td colSpan={8} style={{ fontStyle: 'italic' }}>Balance brought forward</td>
                  <td className="fl-num fl-balance-cell">{fmt(data.opening_balance)}</td>
                </tr>
              )}
              {visibleTransactions.length === 0 ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 'var(--spacing-2xl)', color: 'var(--color-text-secondary)' }}>
                  {!companyId ? 'Select a company' : transactions.length === 0 ? 'No transactions yet' : 'No transactions match the current filters'}
                </td></tr>
              ) : visibleTransactions.map((t) => (
                <tr key={t.id}>
                  <td>{fmtDate(t.date)}</td>
                  <td>{t.company_name || '-'}</td>
                  <td><span className="fl-type-chip">{SOURCE_LABELS[t.source_type] || t.source_type}</span></td>
                  <td>
                    {t.direction ? (
                      <span className={`fl-direction fl-direction-${t.direction.toLowerCase()}`}>
                        {t.direction === 'IN' ? '▲' : '▼'} {t.direction}
                      </span>
                    ) : <span className="fl-empty-val">—</span>}
                  </td>
                  <td>{t.reference_no || '—'}</td>
                  <td><span className="fl-remark-text">{t.remark || '—'}</span></td>
                  <td className="fl-num">{money(t.debit, 'fl-debit-amount')}</td>
                  <td className="fl-num">{money(t.credit, 'fl-credit-amount')}</td>
                  <td className="fl-num fl-balance-cell">{fmt(t.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default SalaryDeduction;
